// lib
import React from 'react';
import { Field } from 'react-final-form';
import styled from 'styled-components';
import map from 'lodash/map';
import { Country } from 'country-state-city';

// twicapp-ui
import Checkbox from '@joinforma/ui/components/Checkbox';
import TextField from '@joinforma/ui/components/TextField';
import Body2 from '@joinforma/ui/components/Body2';
import Select from '@joinforma/ui/components/Select';
import Option from '@joinforma/ui/components/Option';
import Title2 from '@joinforma/ui/components/Title2';
import Caption from '@joinforma/ui/components/Caption';

// src
import { renderCountryName } from 'utils/employee';
import { validateShorterThan } from 'utils/validate';

// Max len of combined address lines 1 & 2 from UPS (has lowest allowed char count)
const ADDRESS_LINE_MAX_LEN = 30;

export const isAddressLineValid = (input: string): string | undefined =>
  validateShorterThan({
    input,
    rule: ADDRESS_LINE_MAX_LEN,
    err_msg: `Your address cannot be longer than ${ADDRESS_LINE_MAX_LEN} characters`,
  });

const CheckboxStyled = styled(Checkbox)`
  margin: 0 0 21px 15px;
  span {
    span {
      margin-left: 27px;
    }
  }
`;

const AddressRow = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

const AddressFieldStyled = styled(TextField)`
  width: 100%;
  &:not(:first-child) {
    margin-left: 23px;
  }
`;

const SelectStyled = styled(Select)`
  width: 100%;
`;

const ClearAddressActionStyled = styled(Body2)`
  margin-bottom: 12px;
  color: ${({ theme }) => theme?.colors?.text?.negative};
  cursor: pointer;
`;

const SubtitleStyled = styled(Title2)`
  color: ${({ theme }) => theme?.colors?.text?.secondary};
  display: flex;
  flex-direction: column;
  flex: 0 0 160px;
`;

const CaptionStyled = styled(Caption)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.tertiary};
  padding-bottom: 14px;
  margin-bottom: 28px;

  color: ${({ theme }) => theme?.colors?.text?.secondary};
`;

const countries = Country.getAllCountries();

type MailingAddressProps = {
  mailing_address_description_placeholder: string;
  is_primary_residence_present: boolean | number;
  onSameAsPrimaryClick: (value: boolean) => void;
  onClearAddressClick: () => void;
};

const RenderAddressField = ({
  name,
  label,
}: {
  name: string;
  label: string;
}) => (
  <Field
    name={name}
    render={({ input, meta }) => (
      <AddressFieldStyled
        {...input}
        label={label}
        error={meta.touched && meta.error}
      />
    )}
    validate={isAddressLineValid}
  />
);

export const RenderMailingAddress = ({
  mailing_address_description_placeholder,
  is_primary_residence_present,
  onSameAsPrimaryClick,
  onClearAddressClick,
}: MailingAddressProps) => (
  <>
    {is_primary_residence_present ? (
      <Field
        name="same_as_primary_address"
        type="checkbox"
        render={({ input }) => (
          <CheckboxStyled {...input} onChange={onSameAsPrimaryClick}>
            <SubtitleStyled>Same as primary residence</SubtitleStyled>
          </CheckboxStyled>
        )}
      />
    ) : (
      ''
    )}
    <AddressRow>
      <RenderAddressField name={'street'} label={'Mailing address line 1'} />
      <RenderAddressField
        name={'street_ext'}
        label={'Mailing address line 2 (optional)'}
      />
    </AddressRow>
    <AddressRow>
      <RenderAddressField name={'locality'} label={'City'} />
      {/* state is required only if country is us */}
      {/* use select field for this */}
      <RenderAddressField name={'state'} label={'State'} />
      <RenderAddressField name={'zip'} label={'Zip code'} />
    </AddressRow>
    <AddressRow>
      <Field
        name={'country'}
        render={({ input }) => (
          <SelectStyled {...input} label={'Country'}>
            {map(countries, ({ isoCode }) => (
              <Option key={isoCode} value={isoCode?.toUpperCase()}>
                {renderCountryName(isoCode)}
              </Option>
            ))}
          </SelectStyled>
        )}
      />
    </AddressRow>
    <ClearAddressActionStyled onClick={onClearAddressClick}>
      Clear mailing address fields
    </ClearAddressActionStyled>
    <CaptionStyled>
      {`Your ${mailing_address_description_placeholder} will be shipped to this mailing address by default. 
      It can be different from your primary residence. Adding a mailing address is optional.`}
    </CaptionStyled>
  </>
);
